<template>
  <platform-modal
    v-model="showModal"
    icon="user"
    :width="600"
    :title="$t('modal.organisationUser.create.title')"
    :saving="pendingSave"
    :save-disabled="!isValid"
    @save="save"
    @close="$emit('close')"
  >
    <v-form
      ref="form"
      v-model="formValid"
      class="mt-2"
    >
      <v-select
        v-model="selectedOrganisationId"
        :items="organisations"
        item-text="name"
        item-value="id"
        :label="$t('modal.organisationUser.create.organisationLabel')"
        :rules="[rules.required]"
        outlined
        class="mb-2"
        @change="onOrganisationChange"
      >
        <template #selection="{ item }">
          <div class="d-flex align-center text-truncate">
            <platform-avatar
              :entity="item"
              entity-type="company"
              :size="32"
              class="mr-2 flex-shrink-0"
            />
            <span class="text-truncate">{{ item.name }}</span>
          </div>
        </template>
        <template #item="{ item }">
          <div class="d-flex align-center py-1">
            <platform-avatar
              :entity="item"
              entity-type="company"
              :size="32"
              class="mr-2"
            />
            <span>{{ item.name }}</span>
          </div>
        </template>
      </v-select>

      <v-select
        v-model="selectedUser"
        :items="availableUsers"
        item-text="displayName"
        :loading="loadingUsers"
        :disabled="!selectedOrganisationId"
        :label="$t('modal.organisationUser.create.userLabel')"
        :placeholder="$t('modal.organisationUser.create.userPlaceholder')"
        :no-data-text="$t('modal.organisationUser.create.noUsersAvailable')"
        :rules="[rules.required]"
        item-value="email"
        return-object
        outlined
      >
        <template #selection="{ item }">
          <div class="d-flex align-center text-truncate">
            <platform-avatar
              :entity="item"
              entity-type="user"
              :size="32"
              class="mr-2 flex-shrink-0"
            />
            <span class="text-truncate">{{ item.displayNameWithEmail }}</span>
          </div>
        </template>
        <template #item="{ item }">
          <div class="d-flex align-center py-1">
            <platform-avatar
              :entity="item"
              entity-type="user"
              :size="32"
              class="mr-2"
            />
            <span>{{ item.displayNameWithEmail }}</span>
          </div>
        </template>
      </v-select>
    </v-form>
  </platform-modal>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import PlatformModal from '@/components/PlatformModal.vue'
import PlatformAvatar from '@/components/shared/PlatformAvatar.vue'
import API from '@/services/API'

interface AvailableUser {
  id: string
  email: string
  displayName: string
  displayNameWithEmail: string
}

interface OrganisationInfo {
  id: string
  name: string | null
}

interface ExistingOrganisationUser {
  organisationId: string
  cognitoIdentityId: string
  user: {
    email: string | null
  } | null
}

export default Vue.extend({
  name: 'AddOrganisationUserModal',
  components: {
    PlatformModal,
    PlatformAvatar
  },
  props: {
    organisations: {
      type: Array as PropType<OrganisationInfo[]>,
      required: true
    },
    existingOrganisationUsers: {
      type: Array as PropType<ExistingOrganisationUser[]>,
      required: true
    }
  },
  data () {
    return {
      showModal: true,
      selectedOrganisationId: null as string | null,
      selectedUser: null as AvailableUser | null,
      usersByOrganisationId: {} as Record<string, AvailableUser[]>,
      loadingUsers: false,
      pendingSave: false,
      formValid: false,
      rules: {
        required: (v: any) => !!v || this.$t('validation.required')
      }
    }
  },
  computed: {
    isValid (): boolean {
      return this.formValid && !!this.selectedUser && !!this.selectedOrganisationId
    },
    availableUsers (): AvailableUser[] {
      if (!this.selectedOrganisationId) {
        return []
      }

      // Get users for the selected organisation
      const organisationUsers = this.usersByOrganisationId[this.selectedOrganisationId] ?? []

      // Get emails of users already in the selected organisation
      const existingUserEmails = new Set(
        this.existingOrganisationUsers
          .filter(ou => ou.organisationId === this.selectedOrganisationId)
          .map(ou => ou.user?.email)
          .filter(email => email != null)
      )

      // Filter out existing users and add formatted text
      return organisationUsers
        .filter(user => !existingUserEmails.has(user.email))
        .map(user => ({
          ...user,
          displayNameWithEmail: `${user.displayName} (${user.email})`
        }))
    }
  },
  mounted () {
    // Auto-select first organisation if only one
    if (this.organisations.length === 1) {
      this.selectedOrganisationId = this.organisations[0].id
    }

    // Fetch all available users once
    void this.fetchAvailableUsers()
  },
  watch: {
    showModal (val: boolean): void {
      if (!val) {
        this.$emit('close')
      }
    }
  },
  methods: {
    onOrganisationChange (): void {
      this.selectedUser = null
    },
    async fetchAvailableUsers (): Promise<void> {
      this.loadingUsers = true

      try {
        const response = await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'getAvailableUsers'
          }
        })

        this.usersByOrganisationId = response ?? {}
      } catch (error) {
        console.error('Failed to fetch available users:', error)
        this.$toasted.error(this.$t('modal.organisationUser.create.fetchUsersError') as string)
      } finally {
        this.loadingUsers = false
      }
    },
    async save (): Promise<void> {
      if (!this.selectedUser || !this.selectedOrganisationId) {
        return
      }

      this.pendingSave = true

      try {
        await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'addOrganisationUser',
            organisationId: this.selectedOrganisationId,
            targetUserEmail: this.selectedUser.email
          }
        })

        this.showModal = false
        this.$emit('created')
      } catch (error: any) {
        console.error('Failed to add organisation user:', error)
        const errorMessage = error?.response?.data?.error || this.$t('modal.organisationUser.create.saveError')
        this.$toasted.error(errorMessage)
      } finally {
        this.pendingSave = false
      }
    }
  }
})
</script>

<style lang="scss" scoped>
::v-deep .v-select .v-select__selections input {
  width: 0;
  min-width: 0;
  opacity: 0;
  position: absolute;
}
</style>
